"use client";

import React, { useState, useMemo } from "react";
import { useParams } from "next/navigation";
import { useFetchDmnvts } from "@/apis/queryFunctions/categoryFunctions";
import { ChevronDown, Check, Minus, SlidersHorizontal } from "lucide-react";
import productColors from "@/mockDatas/productColors.json";
import styles from "./filterBar.module.css";

interface FilterBarProps {
  selectedDmnvts: string[];
  onDmnvtChange: (ids: string[]) => void;
  onReset: () => void;
  allowedNganhHang?: string[];
  groupDefinitions?: { ma_nganh_hang: string; name: string }[];
  locale?: string;
}

const DEFAULT_GROUP_DEFINITIONS = (productColors.group_definitions || []) as {
  ma_nganh_hang: string;
  name: string;
}[];

const DEFAULT_GROUP_DEFINITIONS_EN = [
  { ma_nganh_hang: "TRA", name: "Specialty Tea" },
  { ma_nganh_hang: "CAFE", name: "Coffee" },
  { ma_nganh_hang: "NS", name: "Clean Agricultural Products" },
  { ma_nganh_hang: "TPCB", name: "Processed Foods" },
  { ma_nganh_hang: "NGK", name: "Soft Drinks" },
  { ma_nganh_hang: "DA", name: "Cooking Oil" },
];

export default function FilterBar({
  selectedDmnvts,
  onDmnvtChange,
  onReset,
  allowedNganhHang,
  groupDefinitions,
  locale,
}: FilterBarProps) {
  const params = useParams();
  const currentLocale = locale || (params?.locale as string) || "vi";
  const isEn = currentLocale === "en";

  // 1. Load toàn bộ danh sách nhóm vật tư từ API
  const { data: dmnvts } = useFetchDmnvts({
    condition: { shared: true, status: true },
  });

  // 2. Map theo ma_nganh_hang và loại trừ các ngành hàng không cho phép
  const groupedCategories = useMemo(() => {
    if (!dmnvts || !Array.isArray(dmnvts)) return [];

    const effectiveAllowed =
      allowedNganhHang && allowedNganhHang.length > 0
        ? allowedNganhHang
        : productColors?.allowed_ma_nganh_hang || ["TRA", "CAFE", "NS", "TPCB"];

    const fallbackGroups = isEn
      ? DEFAULT_GROUP_DEFINITIONS_EN
      : DEFAULT_GROUP_DEFINITIONS;

    const baseGroups =
      groupDefinitions && groupDefinitions.length > 0
        ? groupDefinitions
        : fallbackGroups;

    return baseGroups
      .filter((group) => effectiveAllowed.includes(group.ma_nganh_hang))
      .map((group) => {
        const items = dmnvts.filter((item: any) => {
          if (!item?.ma_nganh_hang) return false;
          const itemCode = String(item.ma_nganh_hang).toUpperCase().trim();
          const groupCode = group.ma_nganh_hang.toUpperCase().trim();

          if (itemCode === groupCode) return true;

          const itemNganhHangName = (item.ten_nganh_hang || "").toLowerCase();
          const groupCodeLower = group.ma_nganh_hang.toLowerCase();
          return (
            itemNganhHangName &&
            (itemNganhHangName.includes(groupCodeLower) ||
              itemNganhHangName.includes(group.name.toLowerCase()))
          );
        });

        return {
          ...group,
          items,
        };
      })
      .filter((group) => group.items.length > 0);
  }, [dmnvts, allowedNganhHang]);

  // Expand / collapse nhóm ngành hàng
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const toggleGroupExpand = (groupName: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName]
    );
  };

  // Group level selection: toggle all items in a group
  const handleToggleGroupSelect = (e: React.MouseEvent, group: any) => {
    e.stopPropagation();
    const groupItemIds: string[] = group.items.map((i: any) => i._id);
    if (groupItemIds.length === 0) return;

    const isAllSelected = groupItemIds.every((id) =>
      selectedDmnvts.includes(id)
    );

    if (isAllSelected) {
      onDmnvtChange(selectedDmnvts.filter((id) => !groupItemIds.includes(id)));
    } else {
      const newSelected = Array.from(
        new Set([...selectedDmnvts, ...groupItemIds])
      );
      onDmnvtChange(newSelected);
      if (!expandedGroups.includes(group.name)) {
        setExpandedGroups((prev) => [...prev, group.name]);
      }
    }
  };

  // Single sub-item toggle
  const toggleSubItem = (id: string) => {
    if (selectedDmnvts.includes(id)) {
      onDmnvtChange(selectedDmnvts.filter((d) => d !== id));
    } else {
      onDmnvtChange([...selectedDmnvts, id]);
    }
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.filterCard}>
        {/* Header của Sidebar */}
        <div className={styles.cardHeader}>
          <div className={styles.headerTitleWrap}>
            <div className={styles.headerIconBox}>
              <SlidersHorizontal size={13} />
            </div>
            <h2 className={styles.headerTitle}>
              {isEn ? "Category Filter" : "Bộ Lọc Danh Mục"}
            </h2>
          </div>
          <button
            type="button"
            className={styles.resetButton}
            onClick={onReset}
            title={isEn ? "Reset filters" : "Đặt lại bộ lọc"}
          >
            {isEn ? "Reset" : "Đặt lại"}
          </button>
        </div>

        <div className={styles.cardBody}>
          {/* ═══ FILTER GROUP 1: NGÀNH HÀNG CHÍNH ═══ */}
          <div>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>
                {isEn ? "Main Categories" : "Ngành Hàng Chính"}
              </span>
              <span className={styles.sectionBadge}>
                {groupedCategories.length} {isEn ? "groups" : "nhóm"}
              </span>
            </div>

            <div className={styles.categoryTree}>
              {groupedCategories.map((group) => {
                const groupItemIds = group.items.map((item: any) => item._id);
                const isAllSelected =
                  groupItemIds.length > 0 &&
                  groupItemIds.every((id: string) =>
                    selectedDmnvts.includes(id)
                  );
                const selectedCountInGroup = groupItemIds.filter((id: string) =>
                  selectedDmnvts.includes(id)
                ).length;
                const isSomeSelected =
                  selectedCountInGroup > 0 && !isAllSelected;
                const isExpanded = expandedGroups.includes(group.name);

                return (
                  <div key={group.name} className={styles.groupContainer}>
                    {/* Hàng ngành hàng (Cha) */}
                    <div
                      className={`${styles.groupRow} ${isExpanded ? styles.activeGroup : ""}`}
                      onClick={() => toggleGroupExpand(group.name)}
                    >
                      <div className={styles.groupLeft}>
                        {/* Checkbox cấp ngành hàng */}
                        <div
                          className={`${styles.checkbox} ${isAllSelected ? styles.checked : ""} ${isSomeSelected ? styles.indeterminate : ""}`}
                          onClick={(e) => handleToggleGroupSelect(e, group)}
                          title={
                            isAllSelected
                              ? isEn
                                ? `Deselect all ${group.name}`
                                : `Bỏ chọn toàn bộ ${group.name}`
                              : isEn
                                ? `Select all ${group.name}`
                                : `Chọn toàn bộ ${group.name}`
                          }
                          role="checkbox"
                          aria-checked={
                            isAllSelected
                              ? "true"
                              : isSomeSelected
                                ? "mixed"
                                : "false"
                          }
                        >
                          {isAllSelected && (
                            <Check
                              size={10}
                              strokeWidth={2.8}
                              className={styles.checkIcon}
                            />
                          )}
                          {isSomeSelected && (
                            <Minus
                              size={10}
                              strokeWidth={2.8}
                              className={styles.minusIcon}
                            />
                          )}
                        </div>

                        <span className={styles.groupName}>{group.name}</span>
                      </div>

                      <div className={styles.groupRight}>
                        <span className={styles.groupCount}>
                          {selectedCountInGroup > 0
                            ? `${selectedCountInGroup}/${group.items.length}`
                            : group.items.length}
                        </span>
                        <button
                          type="button"
                          className={`${styles.chevronBtn} ${isExpanded ? styles.expanded : ""}`}
                          aria-label={
                            isExpanded
                              ? isEn
                                ? "Collapse"
                                : "Thu gọn"
                              : isEn
                                ? "Expand"
                                : "Mở rộng"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroupExpand(group.name);
                          }}
                        >
                          <ChevronDown size={13} />
                        </button>
                      </div>
                    </div>

                    {/* Danh sách nhóm con */}
                    {isExpanded && group.items.length > 0 && (
                      <div className={styles.subItems}>
                        {group.items.map((cat: any) => {
                          const isSubSelected = selectedDmnvts.includes(
                            cat._id
                          );
                          return (
                            <div
                              key={cat._id}
                              className={`${styles.subItem} ${isSubSelected ? styles.selectedSub : ""}`}
                              onClick={() => toggleSubItem(cat._id)}
                            >
                              <div
                                className={`${styles.subCheckbox} ${isSubSelected ? styles.subChecked : ""}`}
                              >
                                {isSubSelected && (
                                  <Check
                                    size={9}
                                    strokeWidth={2.8}
                                    className={styles.checkIcon}
                                  />
                                )}
                              </div>
                              <span className={styles.subItemLabel}>
                                {cat.ten_nvt || cat.ten_nganh_hang}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
